import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import './data.css'

/**
 * Datenanzeige fuer ein Intranet, in dem Tabellen der Normalfall sind:
 * Kopfzeile bleibt beim Scrollen stehen, Zahlen laufen in tabellarischen
 * Ziffern, Zeilenhoehe ist knapp gehalten.
 */

export function Table({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableElement> & { children: ReactNode }) {
  return (
    <div className="table-wrap scroll-x">
      <table className={['table', className].filter(Boolean).join(' ')} {...rest}>
        {children}
      </table>
    </div>
  )
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Rechtsbuendig — fuer Zahlen und Mengen. */
  numeric?: boolean
}

export function Th({ numeric, className, children, ...rest }: ThProps) {
  return (
    <th
      scope="col"
      className={className}
      data-numeric={numeric || undefined}
      {...rest}
    >
      {children}
    </th>
  )
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean
}

export function Td({ numeric, className, children, ...rest }: TdProps) {
  return (
    <td className={className} data-numeric={numeric || undefined} {...rest}>
      {children}
    </td>
  )
}

export interface KeyValueItem {
  label: ReactNode
  value: ReactNode
  /** Wert in Monospace — fuer IDs, Sachnummern, Messwerte. */
  mono?: boolean
}

/** Technische Stammdaten als Beschreibungsliste. */
export function KeyValue({ items }: { items: KeyValueItem[] }) {
  return (
    <dl className="keyvalue">
      {items.map((item, index) => (
        <div className="keyvalue__row" key={index}>
          <dt className="keyvalue__key">{item.label}</dt>
          <dd className={`keyvalue__value${item.mono ? ' numeric' : ''}`}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export interface AvatarProps {
  name: string
  src?: string | null
  size?: 'sm' | 'md' | 'lg'
}

/** Initialen aus dem Namen — zwei Buchstaben, Bindestrichnamen zaehlen als einer. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function Avatar({ name, src, size = 'md' }: AvatarProps) {
  return (
    <span className="avatar" data-size={size} title={name}>
      {src ? (
        <img className="avatar__img" src={src} alt="" loading="lazy" />
      ) : (
        <span className="avatar__initials" aria-hidden="true">
          {initials(name)}
        </span>
      )}
      <span className="visually-hidden">{name}</span>
    </span>
  )
}

export interface BreadcrumbItem {
  label: string
  href?: string
}

export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav className="breadcrumbs" aria-label="Brotkrümelnavigation">
      <ol className="breadcrumbs__list">
        {items.map((item, index) => {
          const last = index === items.length - 1
          return (
            <li className="breadcrumbs__item" key={index}>
              {item.href && !last ? (
                <a href={item.href}>{item.label}</a>
              ) : (
                <span aria-current={last ? 'page' : undefined}>{item.label}</span>
              )}
              {!last && (
                <span className="breadcrumbs__sep" aria-hidden="true">
                  /
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
